/**
 * THE EMPTY ORGANISATION'S THREE STEPS, and when a screen shows them instead of itself.
 *
 * A new organisation with no site opened on screens of zeros — "0 of 0 reporting", "Nothing
 * open", an alarm chart with no alarms — which told the person nothing about what to do next
 * (Hamzah, 2026-09-30, a fresh "solar" org on prod). Every screen that follows the header's SITE
 * (`siteScope: 'site'` in nav.ts — the rule, not a hand list) now shows these steps while there is
 * no site at all. Fleet screens (Sites, Integrations) and the admin screens are unchanged.
 */
import type { SiteScope } from './site-context-value';

export type FirstSiteStepId = 'site' | 'gateway' | 'devices';

export interface FirstSiteStep {
  id: FirstSiteStepId;
  done: boolean;
  /** The step the person should do now — the first one not done. */
  next: boolean;
  /** A step waits on the one before it: a feed can only be assigned to a site that exists. */
  blocked: boolean;
}

export function firstSiteSteps(f: {
  siteCount: number;
  assignedFeeds: number;
  integratedDevices: number;
}): FirstSiteStep[] {
  const done: Record<FirstSiteStepId, boolean> = {
    site: f.siteCount > 0,
    gateway: f.assignedFeeds > 0,
    devices: f.integratedDevices > 0,
  };
  const order: FirstSiteStepId[] = ['site', 'gateway', 'devices'];
  const next = order.find((id) => !done[id]) ?? null;
  return order.map((id, i) => ({
    id,
    done: done[id],
    next: id === next,
    blocked: !done[id] && i > 0 && !done[order[i - 1] as FirstSiteStepId],
  }));
}

/**
 * Whether this screen shows the steps instead of itself: a site-scoped screen, once the site list
 * has ANSWERED and answered empty. A failed or pending list is not an empty fleet — the header
 * makes the same distinction ("Sites unavailable") and so must this.
 */
export function showsFirstSite(f: {
  scope: SiteScope;
  loading: boolean;
  error: boolean;
  siteCount: number;
}): boolean {
  return f.scope === 'site' && !f.loading && !f.error && f.siteCount === 0;
}
